import Button from '@components/common/button/Button';
import Modal from '@components/common/modal/Modal';
import { buildValidationSchema, DynamicFormFields } from '@hclinear/hc-linear-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, useTheme } from '@mui/material';
import React, { useMemo, useRef } from 'react';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { imageModalFormFields } from '@/components';
import {
  useGroupPreload,
  useGroupQuery,
  useLoadImageByIdQuery,
  useSaveImageMutation,
} from '@/hooks';
import { ImageDTO, ImageLoadRequest, ImageTypeDTO } from '@/model';
import { ModalProps } from '@/type';

interface ImageModalProps extends ModalProps {
  imageLoadRequest?: ImageLoadRequest;
  elementType?: ImageTypeDTO;
}

const fileNameWithoutExtension = (fileName: string) => fileName.replace(/\.[^.]+$/, '');

const ImageModal = ({
  open,
  handleClose,
  imageLoadRequest,
  elementType,
}: ImageModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data: fetchedImage } = useLoadImageByIdQuery(imageLoadRequest);
  const { data: fetchedGroups = [] } = useGroupQuery();

  const groupIds = useGroupPreload(fetchedImage?.data?.groupIds);

  const { mutateAsync: saveImageAPI, isPending } = useSaveImageMutation();

  const formFields = imageModalFormFields(fetchedGroups, !fetchedImage?.data?.id);
  const formValues = useMemo<Partial<ImageDTO>>(
    () =>
      fetchedImage?.data
        ? fetchedImage.data
        : {
            type: elementType,
            groupIds,
          },
    [fetchedImage?.data, elementType, groupIds],
  );
  const methods = useForm<ImageDTO>({
    values: formValues as ImageDTO,
    resolver: yupResolver(
      buildValidationSchema(formFields) as Yup.ObjectSchema<ImageDTO>,
    ) as Resolver<ImageDTO>,
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;
  // isSubmitting also covers the async validation before the save starts
  const isSaveDisabled = isPending || isSubmitting;

  // the name the last chosen file filled in - replaced by the next file, a name typed by hand is kept
  const autoFilledName = useRef<string | undefined>(undefined);

  // the file field's own input reports only the file's content, its name comes from the change event bubbling up
  const handleFormChange = (event: React.FormEvent<HTMLFormElement>) => {
    const input = event.target as HTMLInputElement;
    const file = input.type === 'file' ? input.files?.[0] : undefined;
    if (!file) {
      return;
    }
    const currentName = methods.getValues('name');
    if (!currentName || currentName === autoFilledName.current) {
      autoFilledName.current = fileNameWithoutExtension(file.name);
      methods.setValue('name', autoFilledName.current, { shouldValidate: true });
    }
  };

  const onSubmit = async (result: ImageDTO) => {
    const { isSuccess } = await saveImageAPI(result);

    if (isSuccess) {
      handleClose(true);
    }
  };

  return (
    <Modal
      title={elementType ? 'common.upload-new-image' : 'common.image'}
      open={open}
      onClose={handleClose}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} onChange={handleFormChange}>
          <DynamicFormFields<ImageDTO>
            formFields={formFields}
            control={methods.control}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '2rem',
            }}
          >
            <Button
              variant='outlined'
              disabled={isSaveDisabled}
              onClick={() => handleClose(false)}
              sx={{
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            >
              {t('common.form.cancel')}
            </Button>
            <Button
              variant='contained'
              type='submit'
              color='primary'
              disabled={isSaveDisabled}
            >
              {t('common.form.submit')}
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default ImageModal;
